/*
 * 敏感欄位名 substring 清單（case-insensitive）。來源：稽核 wp-app 結帳/付款/會員 flow
 * 的 request 與 store state 實際欄位 —— libs/hooks-api/src/types/apiRequest.ts（下單/付款/
 * 登入 request）、core/dni-types/src/data-models/ecommerce-payment.ts（卡片與持卡人 model）、
 * services/cart-services/src/hooks/useWpCartStore.ts（購物車 state）。
 *
 * 取捨：寧可過度遮罩也不漏 PII，故部分單詞會連帶遮非 PII 欄位（如 expiry 連帶遮 expiry_days、
 * state 連帶遮 billingAddressState）— 漏一個個資外洩的成本遠高於少看到幾個診斷欄位。
 * 但代碼型 PII 用完整 key（order_hash / bank_code / account_number）而非單詞，
 * 避免 hash / code / number 誤遮大量診斷欄位。新增欄位前先 grep 確認副作用
 * （如 identifier 會誤遮 applePay / transaction identifier，故不納入）。
 */
const REDACTED_KEY_NAMES = [
  /* 通用 PII / secret */
  'card', 'cvv', 'password', 'phone', 'email', 'token', 'authorization',
  'name', 'address', 'city', 'state', 'postcode', 'note',
  /* 卡片到期：TapPay 用 expire、Frames/CheckoutPay 用 expiry */
  'expire', 'expiry', 'last4',
  /* 台灣電商代碼型 PII（完整 key 避免誤遮） */
  'ubn', 'charity', 'recipient', 'order_hash', 'bank_code', 'account_number',
] as const

const REDACTED_PLACEHOLDER = '[REDACTED]'

const isSensitiveKey = (keyName: string) => {
  const lowerCasedKeyName = keyName.toLowerCase()
  return REDACTED_KEY_NAMES.some(sensitiveKeyName => lowerCasedKeyName.includes(sensitiveKeyName))
}

/* 遞迴遮罩：key 名稱命中 REDACTED_KEY_NAMES（取捨見頂部註解）即換 placeholder，不再遞迴該 subtree */
export const redact = (value: unknown): unknown => {
  if (Array.isArray(value)) {
    return value.map(element => redact(element))
  }
  if (value !== null && typeof value === 'object') {
    const redactedObject: Record<string, unknown> = {}
    for (const [keyName, propertyValue] of Object.entries(value)) {
      redactedObject[keyName] = isSensitiveKey(keyName) ? REDACTED_PLACEHOLDER : redact(propertyValue)
    }
    return redactedObject
  }
  return value
}

/*
 * Datadog 建議 global context + user + feature flags 總和 < 3KiB，超過會發 warning 並可能丟棄。
 * withDatadogLog middleware 的逐一 API attribute 設保守的 2000 字元上限：超過時回傳截斷標記字串而非原物件。
 */
const MAX_CONTEXT_LENGTH = 2000

/*
 * 下單觀測用較大上限：單次 addAction 非 global context 連續累加，不受上述 <3KiB 警告限制，
 * 8 KiB 仍遠低於 Datadog 單一 RUM event 1 MB 上限。
 */
export const MAX_ORDER_PAYLOAD_LENGTH = 8192

export const redactWithSizeCap = (value: unknown, maxLength = MAX_CONTEXT_LENGTH): unknown => {
  try {
    const redactedValue = redact(value)
    const serialized = JSON.stringify(redactedValue)
    /* 無法序列化（undefined / function）就不送，避免繞過遮罩與大小保證 */
    if (serialized === undefined) {
      return undefined
    }
    if (serialized.length > maxLength) {
      return `[TRUNCATED ${serialized.length} chars]`
    }
    return redactedValue
  } catch {
    /* circular reference（redact 遞迴 stack overflow 或 stringify throw）等異常結構：
     * 回標記而非 throw，避免蓋掉呼叫端原始的 error */
    return '[UNSERIALIZABLE]'
  }
}
